import { USqlActivity } from 'app/model/pipeline/usql.activity';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { AzureDataLakeAnalyticsLinkedService } from 'app/model/linkedservice/azure-data-lake-analytics.linkedservice';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { ValueAdapter } from '../value.adapter';
import { FilePath } from 'app/model/dataset/file-path';
import _ from 'lodash';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(USqlActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            scriptPath: '*',
            scriptLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            degreeOfParallelism: '*',
            priority: '*',
            parameters: '*',
            runtimeVersion: '*',
            compilationMode: '*'
        }
    }
})
export class UsqlActivityAdapter extends ActivityAsyncAdapter<USqlActivity, Model.DataLakeAnalyticsUSQLActivity> {
    public model(): IModel {
        return Metamodel.modelFor(USqlActivity);
    }

    protected async _toResource(activity: USqlActivity): Promise<Model.DataLakeAnalyticsUSQLActivity> {
        const modelUSqlActivity: Model.DataLakeAnalyticsUSQLActivity = super._getCommonActivityProperties(activity, 'DataLakeAnalyticsU-SQL') as Model.DataLakeAnalyticsUSQLActivity;
        const usqlActivity: USqlActivity = activity as USqlActivity;
        const scriptPath = ValueAdapter.toResource(usqlActivity, USqlActivity.SCRIPT_PATH);

        let degreeOfParallelism = ValueAdapter.toResource(usqlActivity, USqlActivity.DEGREE_OF_PARALLELISM);
        if (degreeOfParallelism && degreeOfParallelism.type !== 'Expression') {
            degreeOfParallelism = parseInt(degreeOfParallelism, 10);
        }

        let priority = ValueAdapter.toResource(usqlActivity, USqlActivity.PRIORITY);
        if (priority != null && priority.type !== 'Expression') {
            priority = parseInt(priority, 10);
        }

        modelUSqlActivity.typeProperties = {
            scriptPath: scriptPath.type ? scriptPath : scriptPath.path,
            degreeOfParallelism,
            priority,
            runtimeVersion: ValueAdapter.toResource(usqlActivity, USqlActivity.RUNTIME_VERSION),
            compilationMode: ValueAdapter.toResource(usqlActivity, USqlActivity.COMPILATION_MODE),
            scriptLinkedService: usqlActivity.scriptLinkedService != null ? this._toLinkedServiceReferenceModel(usqlActivity.scriptLinkedService) : undefined,
            parameters: toParameterObject(usqlActivity.parameters)
        };

        if (usqlActivity.linkedService) {
            modelUSqlActivity.linkedServiceName = this._toLinkedServiceReferenceModel(usqlActivity.linkedService);
        }

        if (usqlActivity.raw) {
            const parsed = JSON.parse(usqlActivity.raw);
            // Overlay this on u-sql activity
            _.merge(modelUSqlActivity, parsed);
        }

        return modelUSqlActivity;
    }

    protected async _toModel(modelActivity: Model.DataLakeAnalyticsUSQLActivity): Promise<USqlActivity> {
        const activity: USqlActivity = new USqlActivity(modelActivity.name);
        const modelUSqlActivity = modelActivity as Model.DataLakeAnalyticsUSQLActivity;
        this._updatedCommonProperties(modelUSqlActivity, activity);

        if (modelUSqlActivity.typeProperties) {
            if (modelUSqlActivity.typeProperties.scriptPath && modelUSqlActivity.typeProperties.scriptPath.type) {
                ValueAdapter.toModel(activity, USqlActivity.SCRIPT_PATH, modelUSqlActivity.typeProperties.scriptPath);
            } else {
                activity.scriptPath = new FilePath();
                activity.scriptPath.path = modelUSqlActivity.typeProperties.scriptPath;
            }

            if (modelUSqlActivity.typeProperties.degreeOfParallelism) {
                if (modelUSqlActivity.typeProperties.degreeOfParallelism.type === 'Expression') {
                    ValueAdapter.toModel(activity, USqlActivity.DEGREE_OF_PARALLELISM, modelUSqlActivity.typeProperties.degreeOfParallelism);
                } else {
                    activity.degreeOfParallelism = JSON.stringify(modelUSqlActivity.typeProperties.degreeOfParallelism);
                }
            }

            if (modelUSqlActivity.typeProperties.priority) {
                if (modelUSqlActivity.typeProperties.priority.type === 'Expression') {
                    ValueAdapter.toModel(activity, USqlActivity.PRIORITY, modelUSqlActivity.typeProperties.priority);
                } else {
                    activity.priority = JSON.stringify(modelUSqlActivity.typeProperties.priority);
                }
            }

            ValueAdapter.toModel(activity, USqlActivity.RUNTIME_VERSION, modelUSqlActivity.typeProperties.runtimeVersion);
            ValueAdapter.toModel(activity, USqlActivity.COMPILATION_MODE, modelUSqlActivity.typeProperties.compilationMode);
            activity.parameters = toParameterClass(modelUSqlActivity.typeProperties.parameters, activity);
            if (modelUSqlActivity.typeProperties.scriptLinkedService != null) {
                activity.scriptLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                modelUSqlActivity.typeProperties.scriptLinkedService, this.context);
            }
        }

        if (modelUSqlActivity.linkedServiceName != null) {
            activity.linkedService = ReferenceAdapter.toProxyModel<AzureDataLakeAnalyticsLinkedService>(Metamodel.modelFor(AzureDataLakeAnalyticsLinkedService),
            modelUSqlActivity.linkedServiceName, this.context);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelUSqlActivity, USqlActivity.MODEL));

        return activity;
    }
}
